// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { ColumnsType } from "antd/es/table";
import { Select, SelectProps } from "antd";
import { Link, useNavigate } from "react-router-dom";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import WaitingApprovalService from "../../waiting-approval.service";
import KycReview from "../../../../models/kyc-review.model";
import { Status } from "../../../../models/enum";
import ReactCountryFlag from "react-country-flag";
import clsx from "clsx";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  ApprovalGroups,
  BUSINESS_ACCOUNT_BRANCH,
} from "../../../../utils/data/constant";
import CustomSearch from "src/components/custom-search/custom-search";
import { LOADING } from "src/store/actions";
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";
import instance from "src/store/axios";
import { saveAs } from "file-saver";

let searchTimer = null;

const Completed: React.FC = (): ReactElement => {
  const witingApprovalService = new WaitingApprovalService();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const loadingState = useSelector((state: any) => state?.loading);
  const dispatch = useDispatch();
  const [completedRequestsList, setCompletedRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const options: SelectProps["options"] = [
    {
      value: "View change list",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() => navigate("/customers/view-history")}
        >
          <span>View changes list</span>
        </div>
      ),
    },
    {
      value: "Approval history",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() => navigate("/customers/view-history")}
        >
          <span>Approval history</span>
        </div>
      ),
    },
    {
      value: "View KYC Document",
      label: (
        <div className="select-options flex align-center">
          <span>View KYC PDF</span>
        </div>
      ),
    },
  ];

  const handleActionChange = (
    value: string,
    kycReviewId: number,
    kycId: any = null,
    accountId: any = null
  ): void => {
    if (value === "View change list") {
      navigate(`/customers/change-list/${accountId}/${kycId}`);
    } else if (value === "Approval history") {
      navigate(`/customers/view-history/${accountId}`);
    } else if (value === "View KYC Document") {
      downloadDocument(kycReviewId);
    }
  };

  const downloadDocument = async (kycReviewId): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${kycReviewId}/generate-pdf`
      );

      const base64String = response.data?.data;

      // Convert the base64 string to a Blob
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Save the Blob as a PDF file
      saveAs(blob, `KYC.pdf`);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/customers/waiting-approvals`);
    }

    dispatch({ type: LOADING, payload: false });
  };

  useEffect(() => {
    fetchCompletedListData();
  }, [searchWord, sortField, sortOrder]);

  const columns: ColumnsType<KycReview> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Full name",
      dataIndex: "nameEn",
      key: "nameEn",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <span>{record?.kyc?.nameEn || record?.account?.activeKyc?.nameEn}</span>
      ),
      width: 180,
    },
    {
      title: "Account #",
      dataIndex: "accountCode",
      key: "accountCode",
      width: 140,
      sorter: true,
      render: (_, record: KycReview) => (
        <Link
          to={`/home/business-account/${record.accountId}/${record.kycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Nationality",
      dataIndex: "nationality",
      key: "nationality",
      render: (_, record: KycReview) => (
        <div className="flex align-center">
          <ReactCountryFlag
            style={{
              fontSize: "24px",
              marginInlineEnd: "8px",
            }}
            countryCode={
              record?.kyc?.nationality?.countryCode ||
              record?.account?.activeKyc?.nationality?.countryCode
            }
            svg
          />
          <span>
            {record?.kyc?.nationality?.nationalityEn ||
              record?.account?.activeKyc?.nationality?.nationalityEn}
          </span>
        </div>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      render: (_, record: KycReview) => (
        <span>{BUSINESS_ACCOUNT_BRANCH[record?.account?.branch]}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (_, record: KycReview) =>
        record.accountDocumentsId && record?.kycId ? (
          <span>Full review</span>
        ) : record.accountDocumentsId ? (
          <span>Document</span>
        ) : record.kycId ? (
          <span>KYC</span>
        ) : (
          <span>
            {record?.accountDocumentsId && record?.kycId
              ? "Full Review"
              : record?.accountDocumentsId
              ? "Document"
              : "KYC"}
          </span>
        ),
    },
    {
      title: "Status",
      dataIndex: "action",
      key: "action",
      filters: [
        {
          text: "Approved",
          value: "Approved",
        },
        {
          text: "Feedback required",
          value: "FEEDBACK_REQUIRED",
        },
        {
          text: "Rejected",
          value: "Rejected",
        },
      ],
      onFilter: (value, record) => record?.feedback[0]?.status === value,
      render: (_, record) => (
        <div className="flex flex-col gap-2">
          <span
            className={clsx(
              "rounded-md text-sm w-fit px-2 flex items-center justify-center h-[35px] whitespace-nowrap",
              record?.groupAstatus === "Approved"
                ? "bg-gray-600 text-green-500"
                : record?.groupAstatus === "Rejected"
                ? "bg-red-200 text-white"
                : "bg-yellow-500 text-white"
            )}
          >
            Group A:{" "}
            {record?.groupAstatus === "FEEDBACK_REQUIRED"
              ? "Feedback required"
              : record?.groupAstatus}
          </span>
          {record?.groupBstatus && (
            <span
              className={clsx(
                "rounded-md text-sm w-fit px-2 flex items-center justify-center h-[35px] whitespace-nowrap",
                record?.groupBstatus === "Approved"
                  ? "bg-gray-600 text-green-500"
                  : record?.groupBstatus === "Rejected"
                  ? "bg-red-200 text-white"
                  : "bg-yellow-500 text-white"
              )}
            >
              Group B:{" "}
              {record?.groupBstatus === "FEEDBACK_REQUIRED"
                ? "Feedback required"
                : record?.groupBstatus}
            </span>
          )}
          {record?.groupCstatus && (
            <span
              className={clsx(
                "rounded-md text-sm w-fit px-2 flex items-center justify-center h-[35px] whitespace-nowrap",
                record?.groupCstatus === "Approved"
                  ? "bg-gray-600 text-green-500"
                  : record?.groupCstatus === "Rejected"
                  ? "bg-red-200 text-white"
                  : "bg-yellow-500 text-white"
              )}
            >
              Group C:{" "}
              {record?.groupCstatus === "FEEDBACK_REQUIRED"
                ? "Feedback required"
                : record?.groupCstatus}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Action by",
      dataIndex: "approvedBy",
      key: "approvedBy",
      width: 150,
      render: (_, record) => (
        <span>{record?.feedback[0]?.reviewedBy?.name}</span>
      ),
    },
    {
      title: "Action Date",
      dataIndex: "actionDate",
      key: "actionDate",
      width: 270,
      render: (_, record) => (
        <span className="text-gray-500">
          {moment(record?.updatedAt).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      render: (_, record: KycReview) => (
        <span>{record?.kyc?.version || record.accountDocuments?.version}</span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      width: 180,
      render: (text) => (
        <span className="flex justify-center text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "250px",
      render: (_, record) => (
        <div
          className="flex align-center"
          style={{ height: "25px !important" }}
        >
          <Select
            value={null}
            options={getRecordActions(record)}
            style={{ width: "100%", height: "25px !important" }}
            placeholder={<div className="flex">Select an action</div>}
            onChange={(value: string) =>
              handleActionChange(
                value,
                record.id,
                record?.kycId,
                record.accountId
              )
            }
          />
        </div>
      ),
    },
  ];

  const fetchCompletedListData = async (page = 1): Promise<void> => {
    const data: any = await witingApprovalService.getKycReviewListData(
      [Status.Approved, Status.Rejected, Status.FEEDBACK_REQUIRED],
      page,
      searchWord,
      sortField,
      sortOrder
    );

    if (data !== undefined) {
      setResponseDate(data);
      setCompletedRequestsList(data?.reviews);
    }
  };

  const getRecordActions = (record: KycReview) => {
    return (record?.kyc?.version <= 1 &&
      record?.account?.activeKyc?.version <= 1) ||
      !record?.account?.accountCode
      ? options.filter((option) => option.value !== "View change list")
      : options;
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? completedRequestsList?.length : ""} Completed
          requests for{" "}
          {user?.kycReviewGroup
            ? `${ApprovalGroups[user?.kycReviewGroup]}`
            : ""}{" "}
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {completedRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={completedRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchCompletedListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Completed;
